import React from "react";
import { useHistory } from "react-router-dom";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";

// Assets
import cognitiveLogo from "../../../../assets/images/logos/cognitiveTrainingLogo.png";
import dietLogo from "../../../../assets/images/logos/dietLogo.png";
import healthLogo from "../../../../assets/images/logos/healthLogo.png";
import physicalExerciseLogo from "../../../../assets/images/logos/physicalExerciseLogo.png";
import sleepLogo from "../../../../assets/images/logos/sleepLogo.png";

// Hooks
import { useFetchUser } from "../../../../hooks/useFetchUser";
import { useFetchStats } from "../../../../hooks/useFetchStats";
import { Button } from "@mui/material";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

interface Props {
    open: boolean;
    // eslint-disable-next-line
    setOpen(value: boolean): void;
    topic: string;
}

// eslint-disable-next-line
const SurveyDialog: React.FC<Props> = ({ open, setOpen, topic }) => {
    const { currentParticipant } = useFetchUser();
    const { stats } = useFetchStats();
    const hist = useHistory();

    const isCompleted = (questionnaireName: string) => {
        if (!stats || !currentParticipant) return false;
        return stats.some(
            x =>
                x.questionnaireName === questionnaireName &&
                (!currentParticipant.resetDate ||
                    (currentParticipant.resetDate && x.createdAt && x.createdAt.getTime() >= currentParticipant.resetDate?.getTime()))
        );
    };

    const hasQuestionnaire = (name: string) =>
        currentParticipant?.questionnaires?.some(q => q.name === name);

    const QuestionnaireItem: React.FC<{ name: string; label: string; logo: string }> = ({ name, label, logo }) => {
        if (!hasQuestionnaire(name)) return null;
        const completed = isCompleted(name);
        return (
            <div className="surveyDialog__item" onClick={() => hist.push(`/survey/${name}`)}>
                <img src={logo} alt="icon" className="surveyDialog__logo nofilterimg" />
                <p style={{ flex: 1 }}>{label}</p>
                <span
                    style={{
                        backgroundColor: completed ? "green" : "orange",
                        borderRadius: 50,
                        width: 14,
                        height: 14,
                        display: "inline-block",
                        marginLeft: 8,
                        flexShrink: 0,
                    }}
                />
                {completed && <span style={{ fontSize: 12, marginLeft: 6, opacity: 0.7 }}>Completed</span>}
            </div>
        );
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={() => setOpen(false)}
            aria-describedby="alert-dialog-slide-description"
            className="surveyDialog"
        >
            <DialogTitle>Questionnaires available</DialogTitle>
            <DialogContent>
                {currentParticipant &&
                    (!currentParticipant.questionnaires || !currentParticipant.questionnaires.length) && (
                        <p>None</p>
                    )}

                {/* Physical */}
                <QuestionnaireItem name="lawtonBrodyIADL" label="Lawton Brody IADL" logo={physicalExerciseLogo} />
                <QuestionnaireItem name="fallsAndBalance" label="Falls And Balance" logo={physicalExerciseLogo} />
                <QuestionnaireItem name="womenHealth" label="Women Health" logo={physicalExerciseLogo} />

                {/* Sleep */}
                <QuestionnaireItem name="insomniaSeverityIndex" label="Insomnia Severity Index" logo={sleepLogo} />

                {/* Diet */}
                <QuestionnaireItem name="mediterraneanDietAdherence" label="Mediterranean Diet Adherence" logo={dietLogo} />
                <QuestionnaireItem name="dietHistory" label="Diet History" logo={dietLogo} />
                <QuestionnaireItem name="eatingPattern" label="Eating Pattern" logo={dietLogo} />

                {/* Health */}
                <QuestionnaireItem name="healthResourceUtilization" label="Health Resource Utilization" logo={healthLogo} />
                <QuestionnaireItem name="qualityOfLife" label="Quality of life" logo={healthLogo} />
                <QuestionnaireItem name="healthUtilityIndex" label="Health Utility Index" logo={healthLogo} />
                <QuestionnaireItem name="healthBehaviorsQuestionnaire" label="Health Behaviors Questionnaire" logo={healthLogo} />
                <QuestionnaireItem name="healthStatus" label="Health Status Questionnaire" logo={healthLogo} />

                {/* Cognitive */}
                <QuestionnaireItem name="preCognitiveExpectancy" label="Pre-Cognitive Expectancy" logo={cognitiveLogo} />
                <QuestionnaireItem name="postCognitiveExpectancy" label="Post-Cognitive Expectancy" logo={cognitiveLogo} />
                <QuestionnaireItem name="generalizedAnxietyDisorder" label="Generalized Anxiety Disorder" logo={cognitiveLogo} />
                <QuestionnaireItem name="geriatricDepressionScale" label="Geriatric Depression Scale" logo={cognitiveLogo} />
                <QuestionnaireItem name="revisedHearingHandicapInventory" label="Revised Hearing Handicap Inventory" logo={cognitiveLogo} />
                <QuestionnaireItem name="visionHearing" label="Vision and Hearing" logo={cognitiveLogo} />
                <QuestionnaireItem name="bhproQuestionnaires" label="BHP pro questionnaires" logo={cognitiveLogo} />
            </DialogContent>
            <DialogActions>
                <Button fullWidth className="cta__submit" onClick={() => setOpen(false)}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SurveyDialog;
